class UserTracker {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
    this.initializeClarity();
    this.initializeGoogleAnalytics();
  }

  initializeClarity() {
    // Only run on production (not localhost)
    if (window.location.hostname !== 'localhost') {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = `
        (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "t1x5jlf25v");
      `;
      document.head.appendChild(script);
      
      // Set custom session data
      setTimeout(() => {
        if (window.clarity) {
          window.clarity('set', 'sessionId', this.sessionId);
          window.clarity('set', 'portfolioSection', 'main');
          window.clarity('set', 'hostingPlatform', this.getHostingPlatform());
        }
      }, 1000);
    }
  }

  initializeGoogleAnalytics() {
    // Only run on production (not localhost)
    if (window.location.hostname !== 'localhost') {
      // Add Google Analytics 4 script
      const gaScript = document.createElement('script');
      gaScript.async = true;
      gaScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-63XHR944HQ';
      document.head.appendChild(gaScript);

      // Initialize gtag with cross-domain tracking
      const gtagScript = document.createElement('script');
      gtagScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        
        gtag('config', 'G-63XHR944HQ', {
          // Enable cross-domain tracking
          linker: {
            domains: [
              'lakhdeep.vercel.app', 
              'lakhdeep.netlify.app', 
              'lakhdeepjawandha.github.io',
              'lakhdeep.is-a.dev'
            ]
          },
          // Enhanced custom mapping
          custom_map: {
            'custom_parameter_1': 'hostname',
            'custom_parameter_2': 'sessionId',
            'custom_parameter_3': 'platform'
          },
          // Add session ID for enhanced tracking
          session_id: '${this.sessionId}'
        });

        // Send custom event with domain info
        gtag('event', 'domain_visit', {
          'hostname': window.location.hostname,
          'domain_type': window.location.hostname.includes('vercel') ? 'vercel' : 
                         window.location.hostname.includes('netlify') ? 'netlify' : 
                         window.location.hostname.includes('github') ? 'github' :
                         window.location.hostname.includes('is-a.dev') ? 'is-a-dev' : 'other',
          'session_id': '${this.sessionId}',
          'enhanced_tracking': true
        });
      `;
      document.head.appendChild(gtagScript);
    }
  }

  getHostingPlatform() {
    const hostname = window.location.hostname;
    if (hostname.includes('vercel')) return 'vercel';
    if (hostname.includes('netlify')) return 'netlify';
    if (hostname.includes('github')) return 'github';
    if (hostname.includes('is-a.dev')) return 'is-a-dev';
    return 'other';
  }

  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Geolocation is requested at most once per browser. The decision (granted/
  // denied/prompted) is stored in localStorage so we never re-prompt visitors.
  // We also delay the prompt until the user has interacted with the page so
  // it doesn't pop the permission dialog the moment they land.
  scheduleGeolocationRequest() {
    if (typeof window === 'undefined' || !navigator.geolocation) return;

    const STORAGE_KEY = 'geo_consent_v1';
    const DELAY_MS = 30000;

    let stored = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch (_) {
      // localStorage may be unavailable (private mode, blocked) — skip silently.
      return;
    }

    if (stored) return;

    let triggered = false;
    const requestOnce = () => {
      if (triggered) return;
      triggered = true;
      cleanup();
      this.requestGeolocation(STORAGE_KEY);
    };

    const interactionEvents = ['scroll', 'click', 'keydown', 'touchstart'];
    const onInteraction = () => requestOnce();
    interactionEvents.forEach((evt) =>
      window.addEventListener(evt, onInteraction, { once: true, passive: true })
    );
    const timer = setTimeout(requestOnce, DELAY_MS);

    function cleanup() {
      clearTimeout(timer);
      interactionEvents.forEach((evt) =>
        window.removeEventListener(evt, onInteraction)
      );
    }
  }

  requestGeolocation(storageKey) {
    const persist = (value) => {
      try {
        localStorage.setItem(storageKey, value);
      } catch (_) {}
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        persist('granted');
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
        this.sendToAnalytics({
          type: 'geolocation',
          location,
          timestamp: new Date().toISOString(),
          sessionId: this.sessionId,
          hostingPlatform: this.getHostingPlatform(),
        });
        if (window.gtag) {
          window.gtag('event', 'geolocation_granted', {
            session_id: this.sessionId,
            latitude: location.latitude,
            longitude: location.longitude,
          });
        }
      },
      (error) => {
        persist(error && error.code === 1 ? 'denied' : 'unavailable');
      },
      { timeout: 10000, enableHighAccuracy: false, maximumAge: 60 * 60 * 1000 }
    );
  }

  async collectUserData() {
    const data = {
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      colorDepth: window.screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      referrer: document.referrer,
      currentUrl: window.location.href,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      hostingPlatform: this.getHostingPlatform(),
    };

    return data;
  }

  async sendToAnalytics(data) {
    try {
      // Only send to API if it's available (Vercel)
      if (this.getHostingPlatform() === 'vercel') {
        await fetch('/api/analytics', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data)
        });
      }

      // Send custom data to Clarity (works everywhere)
      if (window.clarity) {
        window.clarity('set', 'customUserData', {
          sessionId: data.sessionId,
          platform: data.platform,
          screenResolution: data.screenResolution,
          language: data.language,
          timezone: data.timezone,
          hostingPlatform: data.hostingPlatform
        });
      }

      // Send to Google Analytics 4 with cross-domain data
      if (window.gtag) {
        window.gtag('event', 'enhanced_tracking', {
          session_id: data.sessionId,
          user_agent: data.userAgent,
          screen_resolution: data.screenResolution,
          platform: data.platform,
          language: data.language,
          timezone: data.timezone,
          hosting_platform: data.hostingPlatform,
          viewport: data.viewport,
          color_depth: data.colorDepth,
          online_status: data.onlineStatus,
          referrer: data.referrer
        });
      }
    } catch (error) {
      console.error('Analytics tracking failed:', error);
    }
  }

  async trackPageView() {
    const userData = await this.collectUserData();
    await this.sendToAnalytics(userData);
    
    // Track page view in Clarity
    if (window.clarity) {
      window.clarity('event', 'enhanced_page_view', {
        page: window.location.pathname,
        sessionId: userData.sessionId,
        hostingPlatform: userData.hostingPlatform
      });
    }

    // Track enhanced page view in Google Analytics 4
    if (window.gtag) {
      window.gtag('event', 'enhanced_page_view', {
        session_id: userData.sessionId,
        page_title: document.title,
        page_location: window.location.href,
        hosting_platform: userData.hostingPlatform,
        custom_session_id: userData.sessionId,
        screen_resolution: userData.screenResolution,
        platform: userData.platform
      });
    }
  }

  trackEvents() {
    // Track click events
    document.addEventListener('click', (event) => {
      const elementText = event.target.textContent?.slice(0, 50) || event.target.tagName;
      const hostingPlatform = this.getHostingPlatform();
      
      // Track in Clarity
      if (window.clarity) {
        window.clarity('event', 'enhanced_click', {
          element: event.target.tagName,
          text: elementText,
          sessionId: this.sessionId,
          hostingPlatform: hostingPlatform
        });
      }

      // Track in Google Analytics 4
      if (window.gtag) {
        window.gtag('event', 'enhanced_click', {
          event_category: 'engagement',
          event_label: elementText,
          element_type: event.target.tagName,
          session_id: this.sessionId,
          hosting_platform: hostingPlatform
        });
      }

      // Track in your API
      this.sendToAnalytics({
        type: 'click',
        element: event.target.tagName,
        text: elementText,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        hostingPlatform: hostingPlatform
      });
    });

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      const isVisible = !document.hidden;
      const hostingPlatform = this.getHostingPlatform();
      
      if (window.clarity) {
        window.clarity('event', 'visibility_change', {
          visible: isVisible,
          sessionId: this.sessionId,
          hostingPlatform: hostingPlatform
        });
      }

      if (window.gtag) {
        window.gtag('event', 'page_visibility', {
          event_category: 'engagement',
          event_label: isVisible ? 'visible' : 'hidden',
          session_id: this.sessionId,
          hosting_platform: hostingPlatform
        });
      }

      this.sendToAnalytics({
        type: 'visibility_change',
        visible: isVisible,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        hostingPlatform: hostingPlatform
      });
    });

    // Track scroll events
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollPercent = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
        const hostingPlatform = this.getHostingPlatform();
        
        if (scrollPercent % 25 === 0) {
          if (window.clarity) {
            window.clarity('event', 'scroll_milestone', {
              scrollPercent: scrollPercent,
              sessionId: this.sessionId,
              hostingPlatform: hostingPlatform
            });
          }

          if (window.gtag) {
            window.gtag('event', 'scroll_depth', {
              event_category: 'engagement',
              event_label: `${scrollPercent}%`,
              value: scrollPercent,
              session_id: this.sessionId,
              hosting_platform: hostingPlatform
            });
          }
        }
      }, 500);
    });
  }

  // Track portfolio section views
  trackSectionView(sectionName) {
    const hostingPlatform = this.getHostingPlatform();
    
    if (window.clarity) {
      window.clarity('event', 'section_view', {
        section: sectionName,
        sessionId: this.sessionId,
        hostingPlatform: hostingPlatform,
        timestamp: new Date().toISOString()
      });
    }

    if (window.gtag) {
      window.gtag('event', 'portfolio_section_view', {
        event_category: 'portfolio',
        event_label: sectionName,
        session_id: this.sessionId,
        hosting_platform: hostingPlatform
      });
    }

    this.sendToAnalytics({
      type: 'section_view',
      section: sectionName,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      hostingPlatform: hostingPlatform
    });
  }

  // Track cross-domain events
  trackCrossDomainEvent(eventName, eventData = {}) {
    const enrichedData = {
      ...eventData,
      sessionId: this.sessionId,
      hostingPlatform: this.getHostingPlatform(),
      timestamp: new Date().toISOString()
    };

    // Track in Clarity
    if (window.clarity) {
      window.clarity('event', eventName, enrichedData);
    }

    // Track in Google Analytics 4
    if (window.gtag) {
      window.gtag('event', eventName, {
        event_category: eventData.category || 'cross_domain',
        event_label: eventData.label || eventName,
        value: eventData.value || 1,
        session_id: this.sessionId,
        hosting_platform: this.getHostingPlatform(),
        ...eventData
      });
    }

    // Track in your API
    this.sendToAnalytics({
      type: 'cross_domain_event',
      eventName: eventName,
      eventData: enrichedData,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId
    });
  }
}

if (typeof window !== 'undefined') {
  const tracker = new UserTracker();
  tracker.trackPageView();
  tracker.trackEvents();
  tracker.scheduleGeolocationRequest();

  // Make tracker globally available for section tracking
  window.portfolioTracker = tracker;
}

export default typeof window !== 'undefined' ? new UserTracker() : null;